import React, { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

const STATUSES = [
  { value: 'sold', label: 'Sold', color: '#f59e0b' },
  { value: 'awaiting_production', label: 'Awaiting Production', color: '#8b5cf6' },
  { value: 'in_production', label: 'In Production', color: '#eab308' },
  { value: 'ready_for_install', label: 'Ready for Install', color: '#14b8a6' },
  { value: 'ready_for_pickup', label: 'Ready for Pickup', color: '#3b82f6' },
  { value: 'scheduled', label: 'Scheduled', color: '#3b82f6' },
  { value: 'on_hold', label: 'On Hold', color: '#f97316' },
  { value: 'completed', label: 'Completed', color: '#22c55e' },
  { value: 'cancelled', label: 'Cancelled', color: '#ef4444' }
];

const ON_HOLD_REASONS = [
  { value: 'weather', label: 'Weather prevented install' },
  { value: 'incorrect_product', label: 'Incorrect product' },
  { value: 'material_missing', label: 'Material missing' },
  { value: 'customer_not_ready', label: 'Customer not ready' },
  { value: 'site_issues', label: 'Site not ready' },
  { value: 'crew_incomplete', label: 'Crew couldn\'t complete' },
  { value: 'other', label: 'Other (see notes)' }
];

export default function StatusPicker({ isOpen, onClose, currentStatus, onStatusChange }) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [onHoldReason, setOnHoldReason] = useState('');
  const [showReasonWarning, setShowReasonWarning] = useState(false);

  function handleSave() {
    // Validate "On Hold" requires a reason
    if (selectedStatus === 'on_hold' && !onHoldReason) {
      setShowReasonWarning(true);
      return;
    }

    // Pass both status and reason (if on hold)
    onStatusChange(selectedStatus, onHoldReason || null);
    onClose();
  }

  function handleStatusSelect(status) {
    setSelectedStatus(status);
    setShowReasonWarning(false);
    
    // Clear reason if not "On Hold"
    if (status !== 'on_hold') {
      setOnHoldReason('');
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Job Status" size="small">
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '8px'
          }}>
            Select New Status
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {STATUSES.map(status => (
              <button
                key={status.value}
                onClick={() => handleStatusSelect(status.value)}
                style={{
                  padding: '12px 16px',
                  background: selectedStatus === status.value ? `${status.color}15` : 'var(--bg-card)',
                  border: selectedStatus === status.value ? `2px solid ${status.color}` : '1px solid var(--input-border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: selectedStatus === status.value ? '600' : '400',
                  color: selectedStatus === status.value ? status.color : 'var(--text-primary)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: status.color
                  }} />
                  {status.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* On Hold Reason Dropdown */}
        {selectedStatus === 'on_hold' && (
          <div style={{ marginTop: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Reason for Hold *
            </label>
            <select
              value={onHoldReason}
              onChange={(e) => {
                setOnHoldReason(e.target.value);
                setShowReasonWarning(false);
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: showReasonWarning ? '2px solid var(--status-text-bold-red)' : '1px solid var(--input-border)',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                background: 'var(--bg-tertiary)'
              }}
            >
              <option value="">Select reason...</option>
              {ON_HOLD_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
            {showReasonWarning && (
              <div style={{
                fontSize: '12px',
                color: 'var(--status-text-bold-red)',
                marginTop: '4px',
                fontWeight: '500'
              }}>
                Please select a reason for placing this job on hold
              </div>
            )}
            <div style={{
              fontSize: '11px',
              color: 'var(--text-secondary)',
              marginTop: '6px',
              padding: '8px',
              background: 'var(--note-bg)',
              borderRadius: '6px',
              border: '1px solid var(--note-border)'
            }}>
              ℹ️ This job will show in orange on the calendar and in "Ready for Install" until rescheduled. Customer will be notified via SMS/Email.
            </div>
          </div>
        )}
      </div>

      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid var(--cell-border)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        background: 'var(--bg-secondary)'
      }}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>
          Update Status
        </Button>
      </div>
    </Modal>
  );
}